import { BALL, CAPSULE, FIELD, LASER, RULES, SCORE, VIEW } from './config.js';
import { addScore, showToast, tickToast } from './session.js';
import { followPaddle, releaseBall, setBallAngle, setBallSpeed } from '../entities/ball.js';
import { PADDLE_MODE, laserMuzzles, updatePaddle } from '../entities/paddle.js';
import { DOH_PHASE, damageDoh, updateDoh } from '../entities/doh.js';
import {
  capsuleHitsPaddle,
  createBolt,
  createCapsule,
  shotHitsPaddle,
  updateBolts,
  updateCapsules,
  updateDohShots,
} from '../entities/pickups.js';
import { boltHitsBrick, boltHitsDoh, stepBall } from '../systems/collision.js';
import { applyCapsule, rollCapsule } from '../systems/powerups.js';
import { BRICK_COLORS } from '../render/palette.js';

/**
 * One frame of live gameplay: motion, collisions, capsules, lasers and DOH.
 * Round transitions and screen flow live in game.js; this module only reports
 * what happened through the `fx.events` callbacks.
 */

export function updatePlay(session, dt, fx) {
  session.roundTime += dt;
  session.sinceBrick += dt;
  session.fireCooldown = Math.max(0, session.fireCooldown - dt);
  session.field?.tick(dt);
  session.particles.update(dt);
  tickToast(session, dt);

  updatePaddle(session.paddle, dt, fx.targetX);
  applySpeedDrift(session, dt);
  updateBalls(session, dt, fx);
  updateCapsuleLayer(session, dt, fx);
  updateLaserLayer(session, dt, fx);
  updateBoss(session, dt, fx);
  updateWarp(session, dt, fx);
  breakStalemate(session);

  if (!session.isBoss && session.field?.isCleared()) fx.events.onRoundClear?.();
}

/**
 * Safety net for the rare geometry where the ball settles into a loop that
 * never reaches a brick. After a long dry spell every ball gets bent slightly.
 */
function breakStalemate(session) {
  if (session.sinceBrick < BALL.stallTimeout) return;
  session.sinceBrick = 0;
  for (const ball of session.balls) {
    if (ball.stuck) continue;
    const angle = Math.atan2(ball.vy, ball.vx) + (Math.random() - 0.5) * 0.5;
    setBallAngle(ball, angle);
  }
}

/** The ball creeps faster the longer a round runs. */
function applySpeedDrift(session, dt) {
  for (const ball of session.balls) {
    if (ball.stuck) continue;
    const ceiling = Math.min(
      BALL.maxSpeed,
      BALL.baseSpeed + (session.round - 1) * BALL.speedPerRound + 180,
    );
    if (ball.speed < ceiling) setBallSpeed(ball, ball.speed + BALL.timeRamp * dt);
  }
}

function updateBalls(session, dt, fx) {
  const world = {
    field: session.field,
    paddle: session.paddle,
    doh: session.doh && session.doh.phase !== DOH_PHASE.ENTERING ? session.doh : null,
    events: {
      onWall: () => fx.sfx.wall(),
      onPaddle: (ball, offset) => handlePaddleHit(session, ball, offset, fx),
      onBrick: (brick, result) => handleBrickHit(session, brick, result, fx),
      onDoh: () => handleDohHit(session, fx, 1),
    },
  };

  for (let i = session.balls.length - 1; i >= 0; i -= 1) {
    const ball = session.balls[i];

    if (ball.stuck) {
      followPaddle(ball, session.paddle, dt);
      if (ball.stuckTimer > RULES.catchTimeout) {
        releaseBall(ball, session.paddle);
        fx.sfx.launch();
      }
      continue;
    }

    const alive = stepBall(ball, dt, world);
    if (!alive) {
      session.balls.splice(i, 1);
      session.particles.burst(ball.x, VIEW.H - 6, {
        count: 8,
        color: '#5b6b7b',
        speed: 120,
        life: 0.4,
      });
    }
  }

  if (session.balls.length === 0) fx.events.onBallLost?.();
}

function handlePaddleHit(session, ball, offset, fx) {
  fx.sfx.paddle(typeof offset === 'number' ? offset : 0);
  session.rampHits += 1;

  if (session.rampHits >= BALL.hitsPerRamp) {
    session.rampHits = 0;
    setBallSpeed(ball, ball.speed * BALL.rampFactor);
  }

  if (session.paddle.mode === PADDLE_MODE.CATCH) {
    ball.stuck = true;
    ball.stuckTimer = 0;
    ball.stuckOffset = ball.x - session.paddle.cx;
  }
}

function handleBrickHit(session, brick, result, fx) {
  if (result === 'blocked') {
    fx.sfx.brickBlocked();
    session.particles.spark(brick.x + brick.w / 2, brick.y + brick.h / 2, '#e5c46a');
    return;
  }

  if (result === 'damaged') {
    session.sinceBrick = 0;
    fx.sfx.brickTough();
    session.particles.spark(brick.x + brick.w / 2, brick.y + brick.h / 2, '#c3cbd6');
    return;
  }

  session.sinceBrick = 0;
  fx.sfx.brickBreak(brick.row, session.field.rows);
  session.particles.debris(brick, BRICK_COLORS[brick.code] || BRICK_COLORS.w);
  session.stats.bricks += 1;
  if (addScore(session, brick.points)) fx.events.onExtraLife?.();
  maybeDropCapsule(session, brick, fx);
}

function maybeDropCapsule(session, brick, fx) {
  session.capsuleWarmup += 1;
  if (session.capsules.length > 0) return;
  if (session.capsuleWarmup < CAPSULE.warmup) return;
  if (Math.random() > CAPSULE.dropChance) return;

  const code = rollCapsule(session.round, session);
  if (!code) return;
  session.capsuleWarmup = 0;
  session.capsules.push(
    createCapsule(code, brick.x + brick.w / 2, brick.y + brick.h / 2),
  );
  fx.events.onCapsuleDropped?.(code);
}

function updateCapsuleLayer(session, dt, fx) {
  updateCapsules(session.capsules, dt);

  for (let i = session.capsules.length - 1; i >= 0; i -= 1) {
    const capsule = session.capsules[i];
    if (!capsuleHitsPaddle(capsule, session.paddle)) continue;

    session.capsules.splice(i, 1);
    session.stats.capsules += 1;
    if (addScore(session, SCORE.capsule)) fx.events.onExtraLife?.();
    session.particles.burst(capsule.x, capsule.y, {
      count: 10,
      color: '#ffffff',
      speed: 130,
      life: 0.35,
      size: 2,
    });
    applyCapsule(capsule.type, session, {
      sfx: fx.sfx,
      notify: (text) => showToast(session, text),
    });
    fx.events.onCapsuleTaken?.(capsule.type);
  }
}

function updateLaserLayer(session, dt, fx) {
  updateBolts(session.bolts, dt);

  for (let i = session.bolts.length - 1; i >= 0; i -= 1) {
    const bolt = session.bolts[i];

    if (session.doh && session.doh.phase === DOH_PHASE.ACTIVE && boltHitsDoh(bolt, session.doh)) {
      session.bolts.splice(i, 1);
      session.particles.spark(bolt.x, bolt.y, '#ffb08a');
      handleDohHit(session, fx, 1);
      continue;
    }

    const brick = boltHitsBrick(bolt, session.field);
    if (!brick) continue;

    session.bolts.splice(i, 1);
    const result = session.field.damage(brick);
    session.particles.spark(bolt.x, bolt.y, '#ffb08a');
    handleBrickHit(session, brick, result, fx);
  }
}

function handleDohHit(session, fx, amount) {
  const { doh } = session;
  if (!doh) return;
  const result = damageDoh(doh, amount);
  if (result === 'immune') return;

  fx.sfx.dohHit();
  fx.shake.add(result === 'killed' ? 12 : 4);
  session.particles.burst(doh.cx, doh.y + doh.h / 2, {
    count: result === 'killed' ? 26 : 10,
    color: '#9a9083',
    speed: result === 'killed' ? 240 : 140,
    life: 0.6,
    size: 3,
  });
  if (result === 'killed') fx.events.onDohDefeated?.();
}

function updateBoss(session, dt, fx) {
  const { doh } = session;
  if (!doh) return;

  updateDoh(doh, dt, {
    paddleCx: session.paddle.cx,
    shots: session.dohShots,
    onShoot: () => fx.sfx.dohShoot(),
  });

  updateDohShots(session.dohShots, dt);

  if (doh.phase === DOH_PHASE.DYING) {
    session.dohShots.length = 0;
    return;
  }

  for (let i = session.dohShots.length - 1; i >= 0; i -= 1) {
    const shot = session.dohShots[i];
    if (!shotHitsPaddle(shot, session.paddle)) continue;
    session.dohShots.splice(i, 1);
    fx.events.onPaddleDestroyed?.();
    return;
  }
}

function updateWarp(session, dt, fx) {
  if (!session.warpOpen) return;
  session.warpPhase = Math.min(1, session.warpPhase + dt * 2.6);
  if (session.warping || session.warpPhase < 1) return;

  const paddle = session.paddle;
  // Once the door is fully open Vaus may slide past the right wall.
  paddle.warping = true;

  if (paddle.cx - paddle.w / 2 >= FIELD.right - 6) {
    session.warping = true;
    if (addScore(session, SCORE.warpBonus)) fx.events.onExtraLife?.();
    fx.events.onWarp?.();
  }
}

/** The fire action: launch a held ball, otherwise shoot the lasers. */
export function fire(session, fx) {
  let launched = false;
  for (const ball of session.balls) {
    if (!ball.stuck) continue;
    releaseBall(ball, session.paddle);
    launched = true;
  }
  if (launched) {
    fx.sfx.launch();
    return;
  }

  if (session.paddle.mode !== PADDLE_MODE.LASER) return;
  if (session.fireCooldown > 0) return;

  session.fireCooldown = LASER.cooldown;
  for (const mx of laserMuzzles(session.paddle)) {
    session.bolts.push(createBolt(mx, session.paddle.y - LASER.h));
  }
  fx.sfx.laser();
}
